import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { HelpCircle, ChevronDown, MessageCircle, ArrowRight } from "lucide-react";
import BookCall from "@/components/BookCall";
import { VISITOR_FAQS, MEMBER_FAQS } from "./faqContent";

export const Faq = () => {
  const { user } = useAuth();
  const signedIn = !!user;
  // Members can flip back to the newcomer set — it's the version they hand to prospects.
  const [set, setSet] = useState(null);
  const showMember = set === null ? signedIn : set === "member";
  const faqs = showMember ? MEMBER_FAQS : VISITOR_FAQS;
  const [open, setOpen] = useState(0);

  return (
    <section className="px-6 md:px-10 mt-20" id="faq">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="chip chip-blue mb-4 inline-flex items-center gap-1.5"><HelpCircle size={12} /> FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {showMember ? "Questions our members ask." : "Straight answers, before you commit anything."}
            </h2>
            <p className="text-sm md:text-base text-ink-2 mt-3 max-w-2xl" data-testid="faq-audience-note">
              {showMember
                ? "The operational set — where your numbers come from, why a dash is not a zero, and how money actually reaches you."
                : "No jargon, no hype. If an answer sounds too good, we've written it wrong — tell us."}
            </p>
          </div>
          {signedIn && (
            <div className="flex items-center gap-1 p-1 rounded-full" style={{ background: "rgba(255,255,255,0.55)" }} data-testid="faq-set-toggle">
              {[["member", "Member questions"], ["visitor", "Newcomer questions"]].map(([k, label]) => {
                const active = (k === "member") === showMember;
                return (
                  <button key={k} onClick={() => setSet(k)} data-testid={`faq-set-${k}`}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${active ? "text-white" : "text-ink-2 hover:text-ink"}`}
                    style={active ? { background: "linear-gradient(135deg, var(--primary), var(--primary-2))" } : {}}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4 items-start" data-testid={showMember ? "faq-list-member" : "faq-list-visitor"}>
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="glass p-0 overflow-hidden fade-up" style={{ animationDelay: `${Math.min(i, 6) * 0.03}s` }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-q-${i}`}
                  className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 group">
                  <span className="font-display text-base md:text-lg font-semibold tracking-tight leading-snug">{f.q}</span>
                  <ChevronDown size={18} className="flex-shrink-0 mt-0.5 text-ink-3 transition-transform duration-300"
                    style={{ transform: isOpen ? "rotate(180deg)" : "none" }} />
                </button>
                <div className="grid transition-all duration-300 ease-out" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm text-ink-2 leading-relaxed" data-testid={`faq-a-${i}`}>{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="glass-sm mt-5 px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="faq-cta">
          <div className="text-sm text-ink-2 flex items-center gap-2">
            <MessageCircle size={16} style={{ color: "var(--primary)" }} />
            {showMember ? "Still stuck? Ask Søren inside the app and we'll trace it with you." : "Question we haven't answered? Ask Søren, or ask a human."}
          </div>
          <div className="flex flex-wrap gap-2">
            {showMember
              ? <Link to="/ask" className="btn-secondary" data-testid="faq-cta-ask">Ask Søren <ArrowRight size={15} /></Link>
              : <>
                  <a href="#soren" className="btn-secondary" data-testid="faq-cta-soren">Ask Søren <ArrowRight size={15} /></a>
                  <a href="#signup" className="btn-ghost" data-testid="faq-cta-signup">Request a walkthrough</a>
                </>}
            <BookCall className="btn-ghost" label="Book a call" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faq;
